import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static fallback rates (updated Feb 2026) — used only if live API fails
const FALLBACK_RATES = {
  INR: 1,
  USD: 0.012,
  EUR: 0.011,
  GBP: 0.0095,
  AUD: 0.019,
  CAD: 0.017,
  SGD: 0.016,
  AED: 0.044,
  JPY: 1.82,
  CNY: 0.087,
  CHF: 0.011,
  NZD: 0.020,
  ZAR: 0.23,
  MYR: 0.054,
  THB: 0.43,
};

const CURRENCY_META = {
  INR: { symbol: '₹', name: 'Indian Rupee' },
  USD: { symbol: '$', name: 'US Dollar' },
  EUR: { symbol: '€', name: 'Euro' },
  GBP: { symbol: '£', name: 'British Pound' },
  AUD: { symbol: 'A$', name: 'Australian Dollar' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham' },
  JPY: { symbol: '¥', name: 'Japanese Yen' },
  CNY: { symbol: '¥', name: 'Chinese Yuan' },
  CHF: { symbol: 'Fr', name: 'Swiss Franc' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar' },
  ZAR: { symbol: 'R', name: 'South African Rand' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit' },
  THB: { symbol: '฿', name: 'Thai Baht' },
};

// Client-side country → currency fallback map (mirrors backend)
const countryCurrencyMap = {
  IN: 'INR', US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD', SG: 'SGD',
  AE: 'AED', JP: 'JPY', CN: 'CNY', CH: 'CHF', NZ: 'NZD', ZA: 'ZAR',
  MY: 'MYR', TH: 'THB',
  DE: 'EUR', AT: 'EUR', BE: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR',
  FR: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LT: 'EUR',
  LU: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SK: 'EUR', SI: 'EUR',
  ES: 'EUR', HR: 'EUR',
  PR: 'USD', VI: 'USD', GU: 'USD', AS: 'USD', MP: 'USD',
  EC: 'USD', SV: 'USD', PA: 'USD', MX: 'USD', BR: 'USD',
  AR: 'USD', CL: 'USD', CO: 'USD', PE: 'USD', VE: 'USD',
  GG: 'GBP', JE: 'GBP', IM: 'GBP',
  PK: 'INR', BD: 'INR', LK: 'INR', NP: 'INR',
  ID: 'SGD', PH: 'USD', VN: 'USD',
  SA: 'AED', QA: 'AED', BH: 'AED', KW: 'AED', OM: 'AED',
  KE: 'USD', TZ: 'USD', UG: 'USD', NG: 'USD', EG: 'USD', MA: 'EUR',
};

// localStorage keys
const KEY_MANUAL = 'userCurrency';            // ONLY when user manually picks
const KEY_AUTO = 'autoCurrency';              // auto-detected
const KEY_AUTO_TS = 'autoCurrencyTs';         // when it was auto-detected
const AUTO_TTL = 24 * 60 * 60 * 1000;         // 24 hours

/**
 * Detect user's currency. Priority:
 *  1. Manual user override (never expires unless cleared)
 *  2. Recent auto-detected (< 24h old) — so SPA navigations don't re-hit network
 *  3. Backend /api/geo (server-side detection, no CORS, no ad-blockers)
 *  4. Client-side fallback chain (ipapi.co → ipwho.is → cloudflare trace)
 *  5. USD (default for international audience, NOT INR — site is global)
 */
const detectUserCurrency = async () => {
  // 1. Manual user choice always wins
  const manual = localStorage.getItem(KEY_MANUAL);
  if (manual && countryCurrencyMap.IN) {
    console.log(`💰 Using manual currency: ${manual}`);
    return manual;
  }

  // 2. Recent auto-detection still valid
  try {
    const cachedAuto = localStorage.getItem(KEY_AUTO);
    const cachedTs = parseInt(localStorage.getItem(KEY_AUTO_TS) || '0', 10);
    if (cachedAuto && Date.now() - cachedTs < AUTO_TTL) {
      console.log(`💰 Using cached auto currency: ${cachedAuto}`);
      return cachedAuto;
    }
  } catch (_) { /* ignore */ }

  // 3. Backend geo (most reliable — server has stable IP→country lookup)
  try {
    const res = await axios.get(`${API}/geo`, { timeout: 4000 });
    if (res.data?.detected && res.data?.currency) {
      console.log(`🌍 Backend geo: ${res.data.country_code} → ${res.data.currency}`);
      localStorage.setItem(KEY_AUTO, res.data.currency);
      localStorage.setItem(KEY_AUTO_TS, String(Date.now()));
      return res.data.currency;
    }
  } catch (err) {
    console.warn('Backend geo unreachable, trying client-side:', err.message);
  }

  // 4. Client-side multi-provider fallback (only if backend failed)
  const clientProviders = [
    async () => {
      const r = await fetch('https://ipapi.co/json/');
      const d = await r.json();
      return d.country_code;
    },
    async () => {
      const r = await fetch('https://ipwho.is/');
      const d = await r.json();
      return d.country_code;
    },
    async () => {
      // Cloudflare trace returns plain text "loc=XX"
      const r = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
      const text = await r.text();
      const match = text.match(/loc=([A-Z]{2})/);
      return match ? match[1] : null;
    },
  ];

  for (const provider of clientProviders) {
    try {
      const code = await provider();
      if (code && countryCurrencyMap[code]) {
        const cur = countryCurrencyMap[code];
        console.log(`🌍 Client-side geo: ${code} → ${cur}`);
        localStorage.setItem(KEY_AUTO, cur);
        localStorage.setItem(KEY_AUTO_TS, String(Date.now()));
        return cur;
      }
    } catch (_) { /* try next */ }
  }

  console.warn('All geo providers failed — defaulting to USD');
  return 'USD';
};

// Fetch live rates (INR base). Uses open.er-api.com (free, no key, CORS-friendly).
// Cached in localStorage for 6 hours.
const fetchLiveRates = async () => {
  const CACHE_KEY = 'liveRates_INR';
  const CACHE_TTL = 6 * 60 * 60 * 1000;

  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.ts < CACHE_TTL && parsed.rates) {
        return parsed.rates;
      }
    }
    const response = await fetch('https://open.er-api.com/v6/latest/INR');
    const data = await response.json();
    if (data.result !== 'success' || !data.rates) {
      throw new Error('Invalid rates response');
    }
    const rates = { INR: 1 };
    Object.keys(FALLBACK_RATES).forEach((code) => {
      rates[code] = data.rates[code] !== undefined ? data.rates[code] : FALLBACK_RATES[code];
    });
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), rates }));
    return rates;
  } catch (error) {
    console.error('Live rate fetch failed, using fallback:', error);
    return FALLBACK_RATES;
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState('USD');
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const [detectedCurrency, liveRates] = await Promise.all([
        detectUserCurrency(),
        fetchLiveRates(),
      ]);
      setRates(liveRates);
      setCurrencyState(detectedCurrency);
      setIsLoading(false);
    };
    init();
  }, []);

  // Public setter — when called, marks it as MANUAL user choice (overrides auto)
  const setCurrency = (newCurrency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem(KEY_MANUAL, newCurrency);
  };

  // Allow user to clear manual override and re-trigger auto-detect
  const resetToAutoDetect = async () => {
    localStorage.removeItem(KEY_MANUAL);
    localStorage.removeItem(KEY_AUTO);
    localStorage.removeItem(KEY_AUTO_TS);
    const detected = await detectUserCurrency();
    setCurrencyState(detected);
  };

  const convertPrice = (priceInINR) => {
    const rate = rates[currency] ?? FALLBACK_RATES[currency] ?? 1;
    const converted = priceInINR * rate;
    if (currency === 'JPY') return Math.round(converted);
    return Math.round(converted * 100) / 100;
  };

  const convertToCurrency = (priceInINR, targetCurrency) => {
    const rate = rates[targetCurrency] ?? FALLBACK_RATES[targetCurrency] ?? 1;
    const converted = priceInINR * rate;
    if (targetCurrency === 'JPY') return Math.round(converted);
    return Math.round(converted * 100) / 100;
  };

  const formatPrice = (priceInINR) => {
    const converted = convertPrice(priceInINR);
    const symbol = CURRENCY_META[currency]?.symbol || '';
    return `${symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    })}`;
  };

  const currencies = Object.keys(CURRENCY_META).reduce((acc, code) => {
    acc[code] = {
      ...CURRENCY_META[code],
      rate: rates[code] ?? FALLBACK_RATES[code] ?? 1,
    };
    return acc;
  }, {});

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        resetToAutoDetect,
        currencies,
        rates,
        convertPrice,
        convertToCurrency,
        formatPrice,
        isLoading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
